import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { SelectorDeComuna } from "@/components/informes/SelectorDeComuna";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { useDatosEmpresa, useGuardarDatosEmpresa } from "@/hooks/useDatosEmpresa";
import { getApiErrorMessage } from "@/lib/api";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";

/**
 * Los datos de contacto de Consultek.
 *
 * QUÉ RESUELVE: el correo, el teléfono, el sitio y la dirección de la empresa
 * están hoy QUEMADOS dentro de tres imágenes del documento de cotización —la
 * banda del pie, que se repite en todas las páginas, más la portada y la
 * contraportada—. Cambiar un teléfono es reeditar tres archivos y desplegar.
 * Acá el dato existe una vez y se edita sin desplegar, igual que los párrafos
 * del documento y el prompt de la IA.
 *
 * LA REGIÓN NO SE CAPTURA: sale de la comuna. El registro oficial ya trae la
 * región y su abreviatura —el "RM" que imprime el documento—, así que pedirla
 * sería pedir un dato que el sistema ya tiene y que podría terminar
 * contradiciendo a la comuna elegida.
 *
 * UN SOLO REGISTRO: la empresa es una, así que no hay listado, ni alta, ni
 * borrado. La pantalla es el formulario.
 */

const empresaSchema = z.object({
  email_contacto: z
    .string()
    .trim()
    .min(1, "El correo de contacto es obligatorio.")
    .email("Escribe un correo válido."),
  telefono: z.string().trim().min(1, "El teléfono es obligatorio."),
  // SIN validación de URL, a conciencia: el documento imprime
  // "www.consultek.cl" y exigir `https://` obligaría a guardar una cosa y a
  // imprimir otra. Misma regla que en el request del API.
  sitio_web: z.string().trim().min(1, "El sitio web es obligatorio."),
  direccion: z.string().trim().min(1, "La dirección es obligatoria."),
  comuna_codigo: z.string().min(1, "Elige la comuna de la oficina."),
});

type EmpresaValues = z.infer<typeof empresaSchema>;

const VACIO: EmpresaValues = {
  email_contacto: "",
  telefono: "",
  sitio_web: "",
  direccion: "",
  comuna_codigo: "",
};

export function DatosEmpresa() {
  const datos = useDatosEmpresa();
  const guardar = useGuardarDatosEmpresa();
  const estado = estadoDeConsulta(datos);

  const form = useForm<EmpresaValues>({
    resolver: zodResolver(empresaSchema),
    defaultValues: VACIO,
  });

  /* Los datos llegan DESPUÉS del primer render, así que el formulario nace
     vacío y hay que rellenarlo cuando la consulta responde. Se hace con
     `reset` y no asignando campo por campo: `reset` además vuelve a marcar el
     formulario como limpio, que es lo que evita que la pantalla recién cargada
     se comporte como si tuviera cambios sin guardar.

     Depende de `dataUpdatedAt` y no del objeto: la referencia cambia en cada
     refetch aunque el contenido sea idéntico, y eso pisaría lo que la persona
     está escribiendo. */
  const datosFrescosEn = datos.dataUpdatedAt;
  useEffect(() => {
    if (!datos.data) return;

    form.reset({
      email_contacto: datos.data.email_contacto ?? "",
      telefono: datos.data.telefono ?? "",
      sitio_web: datos.data.sitio_web ?? "",
      direccion: datos.data.direccion ?? "",
      comuna_codigo: datos.data.comuna_codigo ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosFrescosEn]);

  function handleGuardar(valores: EmpresaValues) {
    guardar.mutate(valores, {
      onSuccess: () => toast.success("Datos de la empresa guardados"),
      onError: (error) =>
        toast.error(
          getApiErrorMessage(error, "No pudimos guardar los datos. Inténtalo de nuevo."),
        ),
    });
  }

  return (
    <div className="space-y-6 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div>
        <p className="text-muted-foreground text-caption">
          Datos de contacto de Consultek que se imprimen en el documento de cotización.
        </p>
        <p className="text-ink-hint mt-0.5 max-w-[70ch] text-xs">
          Aparecen en el pie de todas las páginas, en la portada y en la contraportada.
          Un cambio acá vale para las cotizaciones que se emitan después: las ya enviadas
          conservan el documento con que se enviaron.
        </p>
      </div>

      {estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar los datos de la empresa"
          error={datos.error}
          onReintentar={() => datos.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.sinConexion ? (
        <EstadoSinConexion que="los datos de la empresa" />
      ) : estado.cargando ? (
        <div className="bg-card w-full max-w-[35rem] space-y-5 rounded-xl border p-6">
          {[0, 1, 2, 3, 4].map((fila) => (
            <div key={fila} className="space-y-2">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-11 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card w-full max-w-[35rem] rounded-xl border p-6">
          <Form {...form}>
            {/* `noValidate` es obligatorio con `zodResolver`: la validación
                nativa corre ANTES del submit y le gana a zod, así que un
                `type="email"` mal escrito mostraría el globo del navegador
                —otro idioma, fuera del DS— y el `FormMessage` quedaría vacío. */}
            <form onSubmit={form.handleSubmit(handleGuardar)} noValidate className="space-y-5">
              <FormField
                control={form.control}
                name="email_contacto"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Correo de contacto</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="contacto@consultek.cl"
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="telefono"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teléfono</FormLabel>
                    <FormControl>
                      <Input placeholder="56 2 2248 2666" autoComplete="off" {...field} />
                    </FormControl>
                    <FormDescription>
                      Se imprime tal como se escribe acá.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sitio_web"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sitio web</FormLabel>
                    <FormControl>
                      <Input placeholder="www.consultek.cl" autoComplete="off" {...field} />
                    </FormControl>
                    <FormDescription>
                      Sin «https://»: el documento lo imprime como se ve acá.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="direccion"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dirección</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Avda. Del Parque 4928 Of. 126"
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Calle, número y oficina. Si la dirección incluye un parque o
                      edificio, escríbelo acá.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="comuna_codigo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Comuna</FormLabel>
                    <FormControl>
                      <SelectorDeComuna
                        valor={field.value || null}
                        onValorChange={(codigo) => field.onChange(codigo ?? "")}
                        invalido={!!form.formState.errors.comuna_codigo}
                      />
                    </FormControl>
                    <FormDescription>
                      {/* La región no se pide: se deriva. Mostrarla acá es lo que
                          deja ver qué se eligió sin tener que abrir el documento. */}
                      {datos.data?.region
                        ? `Región: ${datos.data.region}${
                            datos.data.abreviatura_region
                              ? ` (${datos.data.abreviatura_region})`
                              : ""
                          }. Se toma de la comuna, no se escribe.`
                        : "La región se toma de la comuna, no se escribe."}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={guardar.isPending} className="gap-2">
                {guardar.isPending && <Spinner className="text-primary-foreground" />}
                Guardar cambios
              </Button>
            </form>
          </Form>
        </div>
      )}
    </div>
  );
}
